import { ProductTypes } from '../../types';
import { AccountGroupType } from '../../types/account-groups/account-group.dto';
import {
  hasPolicyExpired,
  isPolicyInForce,
} from '../../utils/hamburgerMenus/options.utils';

/** What the midterm endorsement rule reads of a policy, as the policy listings send it. */
export interface MidTermEndorsementPolicy {
  readonly product?: string;
  readonly status?: string;
  readonly isEndorsement?: boolean;
  readonly isPrimePlus?: boolean;
  readonly accountGroupType?: string | null;
}

/** Who is asking and which switches are on, which the rule reads beside the policy. */
export interface MidTermEndorsementRules {
  readonly isCowbellUserJumped: boolean;
  /** FORCE_ENABLE_MTE_P100: a P100 policy is endorsable whatever its status. */
  readonly forceEnableMteP100: boolean;
  /** pool_mte: a pool policy is endorsed through the pool flow instead. */
  readonly isPoolMteEnabled: boolean;
  /** False on a surface that carries no pool flow, which then offers this one for a pool policy too. */
  readonly excludePoolPolicies: boolean;
  /**
   * Whether the latest endorsement of an expired P250 policy allows another (`useLatestPrime250PolicyFunctions`). False
   * where it was not read, which offers nothing for an expired P250 policy.
   */
  readonly canEndorseExpiredP250: boolean;
}

/** An endorsement or a Prime Plus policy is never endorsed again here. */
const passesCommonConstraints = (policy: MidTermEndorsementPolicy): boolean =>
  !policy.isEndorsement && !policy.isPrimePlus;

/**
 * Whether the rule needs the latest endorsement of the policy: only for an expired P250 policy shown to a Cowbell user.
 * Reading it for every row would fire one request per policy for brokers who never see the option.
 */
export const needsLatestP250Endorsement = (
  policy: MidTermEndorsementPolicy,
  isCowbellUserJumped: boolean
): boolean =>
  policy.product === ProductTypes.p250 &&
  isCowbellUserJumped &&
  passesCommonConstraints(policy) &&
  hasPolicyExpired(policy.status);

/**
 * Whether a policy is offered a midterm endorsement: a P100 policy in force (or any P100 with FORCE_ENABLE_MTE_P100), a
 * P250 policy in force or expired with an endorsable latest endorsement for a Cowbell user, never an endorsement or a
 * Prime Plus policy, and not a pool policy where the pool flow owns it. The `policy:manage` permission is checked apart.
 */
export function canMidTermEndorse(
  policy: MidTermEndorsementPolicy,
  rules: MidTermEndorsementRules
): boolean {
  if (!passesCommonConstraints(policy)) {
    return false;
  }
  const ownedByPoolFlow =
    rules.excludePoolPolicies &&
    rules.isPoolMteEnabled &&
    policy.accountGroupType === AccountGroupType.POOL;
  if (ownedByPoolFlow) {
    return false;
  }
  if (policy.product === ProductTypes.p100) {
    return isPolicyInForce(policy.status) || rules.forceEnableMteP100;
  }
  if (policy.product === ProductTypes.p250) {
    return (
      rules.isCowbellUserJumped &&
      (isPolicyInForce(policy.status) ||
        (hasPolicyExpired(policy.status) && rules.canEndorseExpiredP250))
    );
  }
  return false;
}

/** Where a midterm endorsement of a policy is made. */
export const midTermEndorsementPath = (
  policyNumber: string,
  product: string
): string => `/agency/policies/${policyNumber}/endorsement/${product}`;
